import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { List, ArrowRight, X, Compass } from "@phosphor-icons/react";
import InviteFriends from "@/components/InviteFriends";
import ListenButton from "@/components/ListenButton";
import LanguageSelector from "@/components/LanguageSelector";
import TranslatedProse from "@/components/TranslatedProse";
import CmsPhotoRail from "@/components/CmsPhotoRail";
import ShareButton from "@/components/ShareButton";

/**
 * LivingRecord — "Building a Living, Real-Time Record of Our Changing Planet"
 *
 * A second long-form editorial companion to /manifesto. Same reading shell
 * (sticky chapter tab-strip, scroll-spy, drop-cap, pull-quotes, listen bar,
 * translate + share tools) with its own eight-chapter body.
 * Route: /article/living-record  (also aliased as /articles/living-record).
 */

const IMG_SATELLITE  = "https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1600&q=70&auto=format&fit=crop"; // earth from space
const IMG_FARMER     = "https://images.unsplash.com/photo-1500937386664-56d1dfef3854?w=1600&q=70&auto=format&fit=crop"; // farmer walking field
const IMG_MANGO      = "https://images.unsplash.com/photo-1553279768-865429fa0078?w=1600&q=70&auto=format&fit=crop"; // mango flowering
const IMG_MAP        = "https://images.unsplash.com/photo-1519681393784-d120267933ba?w=1600&q=70&auto=format&fit=crop"; // starry landscape / map metaphor
const IMG_SCHOOL     = "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1600&q=70&auto=format&fit=crop"; // students / classroom
const IMG_RIVER      = "https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=1600&q=70&auto=format&fit=crop"; // river bend

const CHAPTERS = [
  // ---------------- 01 · Vision ----------------
  {
    id: "vision",
    kicker: "01",
    title: "A record of the planet",
    subtitle: "The idea",
    sections: [
      {
        heading: "What if humanity could document nature as it changes — every day, everywhere?",
        eyebrow: "Introduction",
        lede: true,
        paragraphs: [
          "Humanity has become remarkably good at observing Earth from space. Satellites monitor clouds, rainfall, drought, vegetation, oceans, fires, ice, storms and changes in land use. Governments operate weather stations, universities conduct ecological studies, farmers monitor their crops, and millions of people carry smartphones capable of recording photographs, GPS coordinates, dates and environmental observations.",
          "Yet there is still an enormous missing piece:",
        ],
        pullquote: "We do not have a continuously updated, ground-level, human-observed record of what is happening to nature in every community on Earth.",
        paragraphs2: [
          "NaturePulse.net has the potential to help fill that gap.",
          "The concept is simple.",
        ],
        stack: [
          "Observe locally.",
          "Document continuously.",
          "Understand globally.",
        ],
        outro: "NaturePulse could enable ordinary people — farmers, students, travelers, gardeners, fishermen, hikers, scientists and entire communities — to document what they see happening around them. Every observation could become a small piece of a much larger picture of our changing planet.",
      },
    ],
  },

  // ---------------- 02 · The Weather Gap ----------------
  {
    id: "weather-gap",
    kicker: "02",
    title: "The observation gap",
    subtitle: "What we still don't measure",
    sections: [
      {
        heading: "The Current State of Global Weather Observation",
        eyebrow: "🌍 Global picture",
        image: {
          src: IMG_SATELLITE,
          caption: "Satellites see the planet, but not the ground truth beneath each cloud.",
          credit: "NASA / Unsplash",
        },
        paragraphs: [
          "The world already has an enormous meteorological observation infrastructure. The World Meteorological Organization coordinates international weather and climate observation through networks that include surface weather stations, upper-air observations, satellites, ships, buoys, aircraft, radar and other systems.",
          "But major geographical gaps remain.",
        ],
        meta: [
          ["📅 WMO · Jul 2026", "Across 77 Least Developed Countries and Small Island Developing States, nearly 90% of observations required under the Global Basic Observing Network were still missing."],
          ["🌍 Regions affected", "Africa, parts of Latin America and Asia, and small-island ocean regions"],
          ["📉 Forecast impact", "Closing essential observation gaps could reduce forecast errors by ~30% in Africa and ~20% in the Pacific"],
        ],
        paragraphs2: [
          "This matters because weather has no borders. Atmospheric conditions recorded in one country can eventually affect forecasts thousands of kilometres away.",
        ],
        pullquote: "NaturePulse should not attempt to replace professional meteorological networks. Instead, it can create something complementary.",
      },
    ],
  },

  // ---------------- 03 · Citizen Observation Layer ----------------
  {
    id: "citizen-layer",
    kicker: "03",
    title: "A citizen layer",
    subtitle: "People + place + time",
    sections: [
      {
        heading: "A Global Citizen Environmental Observation Layer",
        eyebrow: "👥 Citizen network",
        paragraphs: [
          "Millions — eventually potentially hundreds of millions — of people could contribute localized environmental observations from places where professional instruments may be sparse.",
          "A NaturePulse observation could automatically record:",
        ],
        chips: [
          "WHAT",
          "WHERE",
          "WHEN",
          "PHOTO / VIDEO",
          "ENVIRONMENTAL CONDITIONS",
        ],
        paragraphs2: [
          "For example:",
        ],
        meta: [
          ["🌳 Subject", "Mango tree"],
          ["🌸 Observation", "Flowering observed"],
          ["📍 Location", "Kochi, Kerala, India"],
          ["🛰 GPS", "Recorded automatically"],
          ["📅 Date", "March 17, 2027 — 7:42 AM"],
          ["🌡 Temperature", "31°C"],
          ["🌧 Recent rainfall", "Yes"],
          ["📷 Photograph", "Attached"],
        ],
        pullquote: "One observation is interesting. Ten years of observations from thousands of mango trees across India could become scientifically valuable.",
      },
    ],
  },

  // ---------------- 04 · Vegetation Observatory ----------------
  {
    id: "vegetation",
    kicker: "04",
    title: "The great vegetation gap",
    subtitle: "What satellites can't see",
    sections: [
      {
        heading: "Satellites Cannot Always Tell the Complete Biological Story",
        eyebrow: "🌿 Ground truth",
        image: {
          src: IMG_MANGO,
          caption: "A satellite sees green. A person underneath knows the tree is flowering three weeks early.",
          credit: "NaturePulse contributor",
        },
        paragraphs: [
          "Satellite imagery has transformed our understanding of vegetation. Satellites can detect vegetation health, drought stress, forest loss, crop development and changes in land cover over enormous areas.",
          "The UN's Food and Agriculture Organization, for example, uses rainfall estimates, vegetation indices and other Earth-observation information to monitor crop conditions and identify agricultural stress. Its Agricultural Stress Index System provides near-real-time information that can help identify agricultural areas experiencing water stress.",
          "But satellites cannot always tell the complete biological story happening on the ground.",
        ],
        pullquote: "A satellite may see that vegetation exists. A person standing underneath it may tell us what plant it is, whether it is flowering, whether fruit has appeared, whether leaves are yellowing, whether insects are attacking it, or whether a species that historically grew there has disappeared.",
      },
      {
        heading: "The NaturePulse Global Vegetation Observatory",
        eyebrow: "🌳 Observatory",
        paragraphs: [
          "NaturePulse could create a continuously growing Global Vegetation Observatory based on GPS-tagged observations. People could document:",
        ],
        ladder: [
          ["Trees.", "Species or local name, flowering, fruiting, new leaves, leaf fall, disease, pests, storm damage, drought stress, fire damage, tree death."],
          ["Fruits.", "Mango, banana, jackfruit, coconut, apple, orange, papaya, guava, grapes, berries — and thousands of regional fruits, from flowering through harvest to end-of-season."],
          ["Vegetables and crops.", "Rice, wheat, corn, millets, cassava, potatoes, tomatoes, onions, beans, leafy vegetables and locally important crops."],
          ["Herbs and wild edibles.", "Edible leaves, herbs, roots, berries, mushrooms and medicinal plants — knowledge that is disappearing in many parts of the world."],
        ],
        outro: "NaturePulse could help preserve generations of local knowledge that rarely reaches major agricultural databases.",
      },
    ],
  },

  // ---------------- 05 · GPS + Time ----------------
  {
    id: "gps-time",
    kicker: "05",
    title: "GPS + time",
    subtitle: "Coordinates become data",
    sections: [
      {
        heading: "GPS Changes Everything",
        eyebrow: "🛰 Location",
        paragraphs: [
          "Every observation should, with the contributor's permission, contain geographic coordinates.",
          "Instead of simply recording:",
        ],
        quotes: [
          "Mangoes are flowering early this year.",
        ],
        paragraphs2: [
          "NaturePulse could record:",
        ],
        meta: [
          ["🌳 Species", "Mango"],
          ["🌸 Observation", "Flowering"],
          ["🛰 Lat/Lon", "GPS captured"],
          ["⛰ Elevation", "84 metres"],
          ["📅 Date", "February 18, 2028"],
          ["📷 Photograph", "Yes"],
          ["🌤 Weather", "Automatically associated"],
          ["✔ Observer", "Verified NaturePulse contributor"],
        ],
        pullquote: "Now multiply this by millions of observations. NaturePulse begins creating a living environmental map.",
      },
      {
        heading: "Time Makes the Data More Valuable",
        eyebrow: "⏳ Chronology",
        paragraphs: [
          "The greatest value may not come from what NaturePulse records today. It may come from comparing today with observations made 5, 10, 20 or 50 years earlier.",
          "Imagine NaturePulse receiving observations from the same region every year. Researchers could eventually ask: when did mango flowering normally begin here in 2027?",
        ],
        stack: [
          "2030",
          "2035",
          "2040",
          "2045",
        ],
        paragraphs2: [
          "Perhaps flowering gradually shifts several weeks earlier. The same analysis could examine bird migrations, butterfly appearances, fruit seasons, crop planting, harvest periods, tree leaf emergence, insect populations, fish catches, rainfall, snowfall, river levels, fires, droughts, flooding — and thousands of other environmental indicators.",
        ],
        pullquote: "NaturePulse would effectively create a planetary environmental diary.",
      },
    ],
  },

  // ---------------- 06 · Climate & Agriculture ----------------
  {
    id: "climate-ag",
    kicker: "06",
    title: "Climate & agriculture",
    subtitle: "From weather to evidence",
    sections: [
      {
        heading: "From Weather Data to Climate Evidence",
        eyebrow: "🌡 Climate",
        image: {
          src: IMG_FARMER,
          caption: "One farmer's memory of when the rain used to arrive — recorded, multiplied, becomes climate evidence.",
          credit: "Unsplash",
        },
        paragraphs: [
          "Weather tells us what is happening today. Climate describes patterns across decades. NaturePulse could help connect the two.",
          "A farmer might report:",
        ],
        quotes: [
          "Rain normally begins here during the first week of June. This year it did not arrive until July.",
        ],
        paragraphs2: [
          "One observation is anecdotal. Thousands of GPS-tagged observations collected consistently over decades become a dataset.",
        ],
        pullquote: "NaturePulse could therefore preserve something that conventional instruments do not always capture: human environmental memory converted into structured data.",
      },
      {
        heading: "Agriculture Could Become More Adaptive",
        eyebrow: "🌾 Adaptation",
        paragraphs: [
          "Climate change will not affect agriculture uniformly. Some areas may become hotter. Others may receive additional rainfall. Some could experience prolonged drought. Growing seasons may shift. Traditional crops may become less productive while different crops become viable.",
          "NaturePulse could help document these transitions from the ground. Consider farmers across several regions reporting:",
        ],
        meta: [
          ["🌾 Crop", "Rice"],
          ["🌱 Variety", "Local variety"],
          ["📅 Planting", "June 4"],
          ["🌧 Rainfall", "Below normal"],
          ["🌾 Harvest", "September 28"],
          ["📉 Yield", "Poor"],
          ["🔎 Reason", "Drought"],
        ],
        paragraphs2: [
          "Over time, AI systems could identify patterns such as: rice yields in this district are declining when planting occurs before the monsoon stabilises. Or: millet varieties are performing better under increasingly dry conditions.",
        ],
        outro: "That information could help agricultural agencies recommend different planting dates, crop varieties, irrigation strategies, water management, fertiliser schedules, pest controls and harvesting periods.",
      },
      {
        heading: "NaturePulse and Global Hunger",
        eyebrow: "🌐 Food security",
        paragraphs: [
          "Food insecurity is closely connected to weather, agriculture, conflict, economics and transportation. FAO already operates the Global Information and Early Warning System on Food and Agriculture (GIEWS), which continuously monitors global food production and alerts decision-makers about emerging shortages.",
          "NaturePulse could eventually provide another useful ground-level signal. Imagine thousands of farmers within a region reporting:",
        ],
        stack: [
          "Rainfall late.",
          "Corn germination poor.",
          "Reservoir levels declining.",
          "Livestock grazing disappearing.",
          "Crop disease increasing.",
        ],
        pullquote: "Instead of waiting until harvest statistics reveal a crisis, these observations might provide early indications that something is going wrong.",
      },
    ],
  },

  // ---------------- 07 · Global Map + AI + Open Data ----------------
  {
    id: "global-map",
    kicker: "07",
    title: "A global map",
    subtitle: "Layers, AI & open data",
    sections: [
      {
        heading: "Imagine a Global NaturePulse Map",
        eyebrow: "🗺 Map",
        image: {
          src: IMG_MAP,
          caption: "Zoom from world down to a single village — then slide time forward.",
          credit: "Unsplash",
        },
        paragraphs: [
          "A visitor could open NaturePulse and view the planet. Zoom into:",
        ],
        stack: [
          "World → Country → State / Province → District / County → City → Village → GPS observation",
        ],
        paragraphs2: [
          "Then select layers:",
        ],
        chips: [
          "🌧 Rain", "🌡 Temperature", "🌱 Vegetation", "🌳 Trees",
          "🌾 Crops", "🍎 Fruits", "🥬 Vegetables", "🌿 Herbs",
          "🌸 Flowering", "🍂 Seasonal", "🐦 Birds", "🦋 Insects",
          "🐟 Fish", "🐾 Wildlife", "🔥 Fires", "🌊 Floods",
          "🏜 Drought", "⛰ Landslides", "❄ Snow & ice", "💧 Rivers",
        ],
        paragraphs3: [
          "Each point could contain photographs, observations and historical comparisons. Move a timeline slider from 2027 → 2030 → 2040 → 2050 and watch environmental conditions change.",
        ],
        pullquote: "AI Could Transform Photographs Into Environmental Data",
      },
      {
        heading: "One Photograph, One Complete Observation",
        eyebrow: "✨ AI",
        paragraphs: [
          "Contributors should not have to complete complicated scientific forms. Someone could simply photograph a plant. NaturePulse AI could suggest:",
        ],
        meta: [
          ["🌳 Plant", "Jackfruit"],
          ["🔬 Scientific name", "Artocarpus heterophyllus"],
          ["🌱 Observation", "Developing fruit"],
          ["🛰 GPS", "Automatically captured"],
          ["📅 Date / time", "Automatically captured"],
          ["⛰ Elevation", "Automatically derived"],
          ["🌤 Weather", "Automatically associated"],
          ["🍃 Season", "Calculated"],
          ["✔ Confidence", "94%"],
        ],
        outro: "The contributor confirms or corrects the information, and the observation becomes part of the database. Expert contributors can later verify uncertain identifications.",
      },
      {
        heading: "A Global Network of Citizen Observers",
        eyebrow: "🌍 Community",
        image: {
          src: IMG_SCHOOL,
          caption: "Schools could adopt a square kilometre — and document it for generations.",
          credit: "Unsplash",
        },
        paragraphs: [
          "NaturePulse could invite participation from farmers, gardeners, schoolchildren, teachers, universities, agricultural colleges, botanical gardens, farm cooperatives, fishermen, forestry departments, environmental organisations, travellers, photographers, hikers, park visitors, indigenous communities and ordinary families.",
        ],
        pullquote: "Schools could adopt a square kilometre. Students could document the same trees, birds, insects, rainfall and seasonal changes year after year — across generations of students.",
      },
      {
        heading: "Open Data for Humanity",
        eyebrow: "🔓 Open data",
        paragraphs: [
          "A fundamental principle should be that appropriately anonymised environmental observations can be made available — with clear licensing, consent, quality controls and privacy safeguards — to organisations working for the public good.",
          "Potential users could include:",
        ],
        stack: [
          "Universities, climate researchers, agricultural scientists.",
          "Meteorological agencies and governments.",
          "Farmers and humanitarian organisations.",
          "Disaster-management agencies and food-security bodies.",
          "Conservation groups and international institutions.",
        ],
        paragraphs2: [
          "NaturePulse could provide APIs allowing approved systems to query location + time + environmental variable + observation type.",
        ],
        outro: "Privacy must remain fundamental. Exact coordinates for private homes, endangered species, sensitive habitats or other vulnerable locations should be obscured or generalised where necessary.",
      },
    ],
  },

  // ---------------- 08 · The 100-Year Vision ----------------
  {
    id: "hundred-year",
    kicker: "08",
    title: "Build for 100 years",
    subtitle: "A living memory of Earth",
    sections: [
      {
        heading: "Satellites + Sensors + People + AI",
        eyebrow: "🧩 The stack",
        paragraphs: [
          "The real opportunity is not to compete with existing environmental systems. It is to connect them.",
        ],
        ladder: [
          ["Satellites.", "See the planet from above."],
          ["Weather stations.", "Measure atmospheric conditions precisely."],
          ["Environmental sensors.", "Continuously measure local physical conditions."],
          ["People.", "Observe biological and environmental events machines may miss."],
          ["Artificial intelligence.", "Connect billions of observations and discover patterns humans cannot easily see."],
        ],
        pullquote: "NaturePulse could become the human observation layer connecting these systems.",
      },
      {
        heading: "NaturePulse Should Build for 100 Years, Not Five",
        eyebrow: "🗓 Long horizon",
        image: {
          src: IMG_RIVER,
          caption: "The photograph you take today only becomes evidence when it is preserved for tomorrow.",
          credit: "Unsplash",
        },
        paragraphs: [
          "The most important design decision may be to think beyond today's website or mobile application. Environmental observations become increasingly valuable as the historical record grows.",
          "A photograph of an ordinary tree taken today may seem insignificant. The same photograph, GPS-tagged and compared against photographs from the same location in 2036, 2046 and 2056, may become evidence of profound ecological change.",
          "NaturePulse should therefore preserve:",
        ],
        stack: [
          "What happened?",
          "Where did it happen?",
          "When did it happen?",
          "What species were involved?",
          "What were the environmental conditions?",
          "Who or what verified the observation?",
          "What photographic or sensor evidence exists?",
        ],
      },
      {
        heading: "A Living Memory of Planet Earth",
        eyebrow: "🌱 Coda",
        paragraphs: [
          "Humanity already generates extraordinary quantities of environmental information. The problem is that much of our everyday observation disappears.",
        ],
        quotes: [
          "A farmer notices that the rains came late.",
          "A grandmother notices that a fruit appears earlier than when she was young.",
          "A fisherman notices that a species has disappeared.",
          "A gardener notices a new insect.",
          "A traveller photographs a shrinking lake.",
          "A student photographs the first flower of spring.",
        ],
        paragraphs2: [
          "Individually, these observations may appear insignificant. Together, properly structured, GPS-tagged, time-stamped and preserved, they could become enormously important.",
          "That is the opportunity for NaturePulse.net.",
        ],
        stack: [
          "Not simply another nature website.",
          "Not simply another weather service.",
          "Not simply another photo-sharing platform.",
          "But a collaborative effort to build a Living Global Environmental Record.",
        ],
        pullquote: "A record created by humanity, for humanity.",
        outro: "If millions of people contribute what they see around them, NaturePulse could help scientists understand environmental change, help farmers adapt agriculture, help governments plan resources, help humanitarian organisations anticipate food shortages, help communities prepare for disasters, and preserve an unprecedented record of how our planet changes from generation to generation.",
      },
      {
        heading: "Every observation matters",
        eyebrow: "🌍 Invitation",
        quotes: [
          "Every observation matters.",
          "Every location tells a story.",
          "Every year adds knowledge.",
        ],
        outro: "Together, humanity can document the changing Earth.",
      },
    ],
  },
];

const flatSectionIds = CHAPTERS.flatMap((c) => c.sections.map((_, i) => `${c.id}-${i}`));

// ---------- Section renderer (mirrors Manifesto.jsx, with a third paragraph block) ----------
function SectionBlock({ chapterId, index, section, dropCap }) {
  return (
    <section
      id={`${chapterId}-${index}`}
      data-testid={`section-${chapterId}-${index}`}
      className="scroll-mt-28 md:scroll-mt-32 mb-16 md:mb-24"
    >
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
        / {section.eyebrow}
      </div>
      <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.05] tracking-tight text-[#151312] mb-6">
        {section.heading}
      </h2>

      {section.image && (
        <figure className="my-6" data-listen-skip>
          <div className="rounded-2xl overflow-hidden border border-black/10 bg-black/5 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]">
            <img
              src={section.image.src}
              alt={section.image.caption}
              loading="lazy"
              className="w-full h-auto block object-cover"
              data-testid={`section-image-${chapterId}-${index}`}
            />
          </div>
          <figcaption className="mt-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] flex flex-wrap items-baseline justify-between gap-2">
            <span className="max-w-[80%] normal-case tracking-normal font-serif-display text-sm text-[#3A3830] italic">
              {section.image.caption}
            </span>
            {section.image.credit && <span>— {section.image.credit}</span>}
          </figcaption>
        </figure>
      )}

      {section.paragraphs?.map((p, i) => (
        <p
          key={`p-${i}`}
          className={`font-serif-display text-lg md:text-xl leading-[1.7] text-[#241209] mb-5 ${
            dropCap && i === 0 ? "first-letter:font-serif-display first-letter:text-[68px] first-letter:leading-[0.85] first-letter:mr-2 first-letter:float-left first-letter:text-[color:var(--np-brand,#B85F2A)]" : ""
          }`}
        >
          {p}
        </p>
      ))}

      {section.stack && (
        <ul className="my-6 space-y-2 border-l-2 border-[color:var(--np-brand,#B85F2A)]/50 pl-5">
          {section.stack.map((s, i) => (
            <li key={i} className="font-serif-display text-lg md:text-xl leading-snug text-[#241209]">
              {s}
            </li>
          ))}
        </ul>
      )}

      {section.meta && (
        <div className="my-6 rounded-2xl border border-black/10 bg-white/70 backdrop-blur p-5 md:p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {section.meta.map(([k, v], i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] whitespace-nowrap">
                {k}
              </div>
              <div className="font-serif-display text-base md:text-lg text-[#151312]">{v}</div>
            </div>
          ))}
        </div>
      )}

      {section.paragraphs2?.map((p, i) => (
        <p key={`p2-${i}`} className="font-serif-display text-lg md:text-xl leading-[1.7] text-[#241209] mb-5">
          {p}
        </p>
      ))}

      {section.quotes && (
        <div className="my-6 space-y-2">
          {section.quotes.map((q, i) => (
            <div
              key={i}
              className="font-serif-display text-lg md:text-xl italic text-[#241209] pl-5 border-l-2 border-[#151312]/20"
            >
              &ldquo;{q}&rdquo;
            </div>
          ))}
        </div>
      )}

      {section.chips && (
        <ul className="flex flex-wrap gap-2 my-6">
          {section.chips.map((c, i) => (
            <li
              key={i}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-black/10 bg-white/60 backdrop-blur font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#241209]"
            >
              {c}
            </li>
          ))}
        </ul>
      )}

      {section.ladder && (
        <div className="my-6 rounded-2xl bg-gradient-to-br from-white to-[#FBF2E1] border border-black/10 p-5 md:p-6">
          <ol className="space-y-3">
            {section.ladder.map(([k, v], i) => (
              <li key={i} className="flex items-baseline gap-4">
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] w-4">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif-display text-lg md:text-xl text-[#151312]">
                  <b className="font-serif-display">{k}</b> {v}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {section.paragraphs3?.map((p, i) => (
        <p key={`p3-${i}`} className="font-serif-display text-lg md:text-xl leading-[1.7] text-[#241209] mb-5">
          {p}
        </p>
      ))}

      {section.pullquote && (
        <blockquote className="my-8 md:my-10 border-y-2 border-[color:var(--np-brand,#B85F2A)]/40 py-6">
          <div className="font-serif-display text-2xl md:text-3xl leading-[1.25] tracking-tight text-[#151312]">
            &ldquo;{section.pullquote}&rdquo;
          </div>
        </blockquote>
      )}

      {section.outro && (
        <p className="font-serif-display text-lg md:text-xl leading-[1.7] text-[#241209]">
          {section.outro}
        </p>
      )}
    </section>
  );
}

// ---------- Main page ----------
export default function LivingRecord() {
  const [active, setActive] = useState(flatSectionIds[0]);
  const [progress, setProgress] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const observed = useRef(new Map());

  useEffect(() => {
    const els = flatSectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => observed.current.set(e.target.id, e));
        const visible = Array.from(observed.current.values())
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));

    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? Math.min(1, Math.max(0, h.scrollTop / total)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { observer.disconnect(); window.removeEventListener("scroll", onScroll); };
  }, []);

  const activeChapterId = active.split("-").slice(0, -1).join("-");

  const jumpTo = (id) => {
    setDrawerOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="bg-[#F5EBDC] text-[#151312] min-h-screen pt-32 md:pt-36 pb-32" data-testid="living-record-page">
      {/* Reading progress bar */}
      <div
        aria-hidden
        className="fixed top-[64px] md:top-[70px] left-0 h-[2px] bg-[color:var(--np-brand,#B85F2A)] z-40"
        style={{ width: `${progress * 100}%` }}
      />

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-4">
          / Essay · A NaturePulse article
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tighter max-w-4xl">
          Building a Living<span className="text-[color:var(--np-brand,#B85F2A)]">,</span> Real-Time Record of Our Changing Planet
        </h1>
        <p className="mt-6 font-serif-display text-lg md:text-xl italic text-[#3A3830] max-w-3xl">
          What if humanity could document nature as it changes — every day, everywhere?
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3" data-listen-skip>
          <Link
            to="/pulse"
            data-testid="living-record-cta-pulse"
            className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
          >
            <Compass size={14} weight="fill" /> Open My Pulse
          </Link>
          <InviteFriends variant="chip" label="Invite an observer" />
        </div>

        {/* Article tools — Listen (TTS) + Language (Translate) + Share */}
        <div
          className="mt-6 flex flex-wrap items-center gap-2 rounded-2xl border border-black/10 bg-white/70 backdrop-blur px-4 py-3"
          data-listen-skip
          data-testid="article-tools"
        >
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mr-1">
            / Tools
          </span>
          <ListenButton label="Listen to article" />
          <LanguageSelector variant="light" />
          <ShareButton
            title="Building a Living, Real-Time Record of Our Changing Planet"
            text="A NaturePulse essay on documenting nature — every day, everywhere."
            hashtags={["NaturePulse", "CitizenScience", "ClimateAction"]}
            variant="chip"
            testid="living-record-share"
          />
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] ml-1">
            20+ languages · read aloud
          </span>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14 md:mt-20 grid grid-cols-12 gap-6 md:gap-10">
        {/* Sticky side-tab TOC (desktop) */}
        <aside className="hidden lg:block col-span-3 xl:col-span-3">
          <nav className="sticky top-28" aria-label="Chapters">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-muted,#3A3830)] mb-3">
              / Chapters
            </div>
            <ol className="space-y-1">
              {CHAPTERS.map((c) => {
                const isActive = activeChapterId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => jumpTo(`${c.id}-0`)}
                      data-testid={`toc-${c.id}`}
                      className={`w-full text-left group flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                        isActive
                          ? "bg-[#151312] text-[#F5EBDC] border-[#151312]"
                          : "bg-transparent text-[#241209] border-transparent hover:bg-white/60 hover:border-black/10"
                      }`}
                    >
                      <span className={`font-mono-tech text-[10px] tracking-[0.28em] mt-0.5 ${isActive ? "text-[#E4A15C]" : "text-[color:var(--np-brand,#B85F2A)]"}`}>
                        {c.kicker}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-serif-display text-base leading-tight">{c.title}</span>
                        <span className={`block font-mono-tech text-[9px] tracking-[0.22em] uppercase mt-1 ${isActive ? "text-[#E5D8C4]/75" : "text-[color:var(--np-muted,#3A3830)]"}`}>
                          {c.subtitle}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-6 pt-6 border-t border-black/10">
              <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">
                Reading progress
              </div>
              <div className="h-1 rounded-full bg-black/10 overflow-hidden">
                <div
                  className="h-full bg-[color:var(--np-brand,#B85F2A)]"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <div className="mt-1 font-mono-tech text-[9px] tracking-[0.22em] text-[color:var(--np-muted,#3A3830)]">
                {Math.round(progress * 100)}%
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-black/10">
              <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">
                Also read
              </div>
              <Link
                to="/manifesto"
                data-testid="cross-link-manifesto"
                className="block font-serif-display text-base leading-tight text-[#151312] hover:text-[color:var(--np-brand,#B85F2A)]"
              >
                A Living Diary of the Planet →
              </Link>
            </div>
          </nav>
        </aside>

        {/* Article */}
        <article className="col-span-12 lg:col-span-9 xl:col-span-8 max-w-[720px]" data-listen-root>
          <TranslatedProse cacheKey="living-record-v1">
          {CHAPTERS.map((c, ci) => (
            <div key={c.id} className="mb-4">
              <div className="mb-8 md:mb-10 flex items-baseline gap-4 border-b border-black/10 pb-3">
                <div className="font-mono-tech text-[11px] tracking-[0.32em] text-[color:var(--np-brand,#B85F2A)]">
                  {c.kicker}
                </div>
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted,#3A3830)]">
                  {c.subtitle}
                </div>
              </div>
              {c.sections.map((s, i) => (
                <SectionBlock
                  key={`${c.id}-${i}`}
                  chapterId={c.id}
                  index={i}
                  section={s}
                  dropCap={ci === 0 && i === 0}
                />
              ))}
            </div>
          ))}

          {/* Community photo library — pulls from the CMS. Silent when empty. */}
          <CmsPhotoRail
            eyebrow="From the community lens"
            title="Photos uploaded to the NaturePulse library"
            limit={12}
          />

          {/* Sign-off */}
          <div className="mt-6 rounded-2xl bg-[#151312] text-[#F5EBDC] p-6 md:p-8">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#E4A15C] mb-3">
              / NaturePulse.net
            </div>
            <div className="font-serif-display text-2xl md:text-3xl leading-[1.15] tracking-tight">
              Observe · Document · Preserve · Understand · Act
            </div>
            <p className="mt-3 font-serif-display text-base md:text-lg text-[#E5D8C4]/80">
              Every observation matters. Every location tells a story. Every year adds knowledge. Together, humanity can document the changing Earth.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/pulse"
                data-testid="living-record-footer-pulse"
                className="inline-flex items-center gap-2 rounded-full bg-[#F5EBDC] text-[#151312] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
              >
                Open My Pulse <ArrowRight size={14} weight="bold" />
              </Link>
              <Link
                to="/manifesto"
                data-testid="living-record-footer-manifesto"
                className="inline-flex items-center gap-2 rounded-full border border-[#E4A15C]/60 text-[#E4A15C] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#E4A15C] hover:text-[#151312]"
              >
                Read the Manifesto <ArrowRight size={14} weight="bold" />
              </Link>
              <InviteFriends variant="chip" label="Invite an observer" />
            </div>
          </div>
          </TranslatedProse>
        </article>
      </div>

      {/* Mobile floating "Chapters" button */}
      <button
        onClick={() => setDrawerOpen(true)}
        data-testid="living-record-toc-toggle"
        className="lg:hidden fixed bottom-24 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase shadow-xl"
      >
        <List size={14} weight="bold" /> Chapters
      </button>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setDrawerOpen(false)}
            data-testid="living-record-toc-backdrop"
          />
          <motion.div
            className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-[#F5EBDC] p-5 pt-4 max-h-[75dvh] overflow-y-auto"
            initial={{ y: 400 }} animate={{ y: 0 }} exit={{ y: 400 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                / Chapters
              </div>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close">
                <X size={16} weight="bold" />
              </button>
            </div>
            <ol className="space-y-1">
              {CHAPTERS.map((c) => {
                const isActive = activeChapterId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => jumpTo(`${c.id}-0`)}
                      data-testid={`toc-mobile-${c.id}`}
                      className={`w-full text-left flex items-start gap-3 px-3 py-3 rounded-lg border ${
                        isActive
                          ? "bg-[#151312] text-[#F5EBDC] border-[#151312]"
                          : "bg-white/70 text-[#241209] border-black/10"
                      }`}
                    >
                      <span className={`font-mono-tech text-[10px] tracking-[0.28em] mt-0.5 ${isActive ? "text-[#E4A15C]" : "text-[color:var(--np-brand,#B85F2A)]"}`}>
                        {c.kicker}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-serif-display text-base leading-tight">{c.title}</span>
                        <span className={`block font-mono-tech text-[9px] tracking-[0.22em] uppercase mt-0.5 ${isActive ? "text-[#E5D8C4]/75" : "text-[color:var(--np-muted,#3A3830)]"}`}>
                          {c.subtitle}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </motion.div>
        </div>
      )}
    </main>
  );
}
